import { AssignmentTurnedIn, Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';

import { cellFormat } from '../../../../../../../../../lib/designSystem/Table/cellFormat';
import { LightTooltip } from '../../../../../../../../../lib/designSystem/WhiteTooltip';
import { isDateAWeekDay } from '../../../../../../../../../lib/functions/dates';
import {
  formatDate,
  formatMoney,
} from '../../../../../../../../../lib/functions/utils';
const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
      minWidth: '1400px',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    tableCell: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '150px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellTasa: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      textAlign: 'right',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellNames: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '100px',
      maxWidth: '120px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableContainer: {
      border: '1px solid #e8e8e8',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'auto',
      maxHeight: '600px',
      overflowY: 'auto',
      overflowX: 'auto',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    tableHead: {
      backgroundColor: '#f8f9fa !important',
      '& th': {
        backgroundColor: '#f8f9fa !important',
        color: '#495057 !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        padding: '16px 8px !important',
        borderBottom: '2px solid #dee2e6',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    textField: {
      fontSize: '13px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '13px',
      },
    },
    textFieldAutocomplete: {
      fontSize: '13px !important',
      minWidth: '120px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '6px 12px',
        fontSize: '13px',
      },
    },
    iconButton: {
      padding: '6px',
      margin: '0 2px',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },
    deleteButton: {
      color: '#dc3545',
      '&:hover': {
        backgroundColor: 'rgba(220, 53, 69, 0.08)',
      },
    },
    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },
    tableRow: {
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transition: 'background-color 0.2s ease',
      },
      '&:nth-of-type(even)': {
        backgroundColor: '#fafbfc',
      },
      '&:nth-of-type(even):hover': {
        backgroundColor: '#f1f3f4',
      },
    },
    amountCell: {
      fontWeight: '600',
      color: '#2c3e50',
    },
    daysCell: {
      fontWeight: '500',
      color: '#6c757d',
    },
    rateCostCell: {
      fontWeight: '600',
      color: '#28a745',
    },
    actionsCell: {
      minWidth: '80px',
      maxWidth: '100px',
      padding: '8px !important',
    },
    chip: {
      fontSize: '11px',
      height: '20px',
      borderRadius: '10px',
    },
    '@media (max-width: 1200px)': {
      tableCell: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '150px',
      },
      tableCellNames: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '100px',
      },
      tableCellTasa: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '60px',
      },
    },
  }),
);

const ResellsTable = (props: any) => {
  const { resells, setResells, funds, canForcePaymentDate } = props;
  const classes = useStyles();

  const fundsCostDict = funds.reduce((acc: any, fund: any) => {
    acc[fund.id] = fund.cost;
    return acc;
  }, {});

  const calculateDays = (start: Date, end: Date) => {
    const days = Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    return days;
  };

  const dateValidators = (date: Date, type: string, rowIndex: number) => {
    if (canForcePaymentDate) {
      const days = calculateDays(
        type === 'startDate' ? date : new Date(resells[rowIndex].startDate),
        type === 'endDate' ? date : new Date(resells[rowIndex].endDate),
      );
      if (days <= 0) {
        alert(
          'La fecha de vencimiento no puede ser menor a la fecha de liberación',
        );
        returnTempDateToActualDate(rowIndex);
        return false;
      }
      return days;
    }
    if (!isDateAWeekDay(date) && type === 'endDate') {
      alert('La fecha de vencimiento debe ser un día hábil');
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    if (date < new Date() && type === 'endDate') {
      alert('La fecha de vencimiento no puede ser menor a la fecha actual');
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    const days = calculateDays(
      type === 'startDate' ? date : new Date(resells[rowIndex].startDate),
      type === 'endDate' ? date : new Date(resells[rowIndex].endDate),
    );
    if (days <= 0) {
      alert(
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    return days;
  };

  const returnTempDateToActualDate = (rowIndex: number) => {
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          tempEndDate: doc.endDate,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeTempDate = (e: any, type: string, rowIndex: number) => {
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          [type]: e.target.value,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeDate = (value: any, type: string, rowIndex: number) => {
    const days = dateValidators(new Date(value), type, rowIndex);
    if (!days) return;
    const resell = resells[rowIndex];
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          [type]: value,
          days,
          rateCost: (doc.amount * parseFloat(resell.rate) * days) / 30 / 100,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeCessionDate = (value: any, rowIndex: number) => {
    const resell = resells[rowIndex];
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          cessionDate: value,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeRate = (e: any, rowIndex: number) => {
    const value = e.target.value;
    const currentRate = resells[rowIndex].rate;

    if (value === '') {
      const newResells = resells.map((doc: any, i: any) => {
        if (i === rowIndex) {
          return {
            ...doc,
            rate: '0',
            rateCost: 0,
          };
        }
        return doc;
      });
      setResells(newResells);
      return;
    }

    let finalValue = value;
    if (currentRate === '0' || currentRate === 0) {
      finalValue = value;
    }

    const isValidFormat = /^\d*\.?\d*$/.test(finalValue);
    if (!isValidFormat) return;

    if (finalValue.endsWith('.')) {
      const newResells = resells.map((doc: any, i: any) => {
        if (i === rowIndex) {
          return {
            ...doc,
            rate: finalValue,
            rateCost:
              (doc.amount * parseFloat(finalValue) * doc.days) / 30 / 100,
          };
        }
        return doc;
      });
      setResells(newResells);
      return;
    }

    const numbered = parseFloat(finalValue);

    if (isNaN(numbered) || numbered < 0 || numbered > 100) return;

    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          rate: finalValue,
          rateCost: (doc.amount * numbered * doc.days) / 30 / 100,
        };
      }
      return doc;
    });

    setResells(newResells);
  };

  const getDisplayValue = (rate: any) => {
    if (!rate || rate === '') {
      return '0';
    }
    return rate.toString();
  };

  const handleChangeOperationCostFixed = (e: any, rowIndex: number) => {
    const value = e.target.value.split('.').join('').split('$').join('');
    if (isNaN(value)) return;
    if (value < 0) return;
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return { ...doc, operationCostFixed: value };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeFund = (e: any, value: any, rowIndex: number) => {
    const newResells = resells.map((doc: any, i: any) => {
      const rate = value ? fundsCostDict[value.id] : 0;
      const rateCost = (doc.amount * rate * doc.days) / 30 / 100;
      if (i === rowIndex) {
        return {
          ...doc,
          fundId: value ? value.id : null,
          fund: value ? value : null,
          rateCost,
          rate,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const assignConditionsForAllResells = () => {
    console.log(resells[0]);
    const newResells = resells.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      const rateCost =
        ((doc.amount * resells[0].rate) / 30 / 100) * resells[0].days;
      return {
        ...doc,
        rate: resells[0].rate,
        rateCost,
        startDate: resells[0].startDate,
        endDate: resells[0].endDate,
        tempEndDate: resells[0].endDate,
        cessionDate: resells[0].cessionDate,
        operationCostFixed: resells[0].operationCostFixed,
        operationCostVariable: resells[0].operationCostVariable,
        fundId: resells[0].fundId,
        fund: resells[0].fund,
        days: resells[0].days,
      };
    });
    console.log(newResells);
    setResells([
      ...newResells.map((resell: any, index: any) => ({
        ...resell,
        key: index,
      })),
    ]); // Ensure a new array reference with unique keys
  };

  const deleteRow = (rowIndex: number) => {
    const newInvoices = resells.filter((doc: any, i: any) => i !== rowIndex);
    setResells(newInvoices);
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.actionsCell}></TableCell>
            <TableCell className={classes.tableCell}>Folio</TableCell>
            <TableCell className={classes.tableCellTasa}>Solicitud</TableCell>
            <TableCell className={classes.tableCellNames}>Cliente</TableCell>
            <TableCell className={classes.tableCellNames}>Deudor</TableCell>
            <TableCell className={classes.tableCell}>Monto Doc.</TableCell>
            <TableCell className={classes.tableCell}>Liberación</TableCell>
            <TableCell className={classes.tableCellTasa}>Tasa (%)</TableCell>
            <TableCell className={classes.tableCell}>Comisión</TableCell>
            <TableCell className={classes.tableCell}>
              Fecha cesión obtenida
            </TableCell>
            <TableCell className={classes.tableCell}>Fecha inicio</TableCell>
            <TableCell className={classes.tableCell}>
              Fecha vencimiento
            </TableCell>
            <TableCell className={classes.tableCell}>Fondo</TableCell>
            <TableCell className={classes.tableCell}>Días</TableCell>
            <TableCell className={classes.tableCell}>
              Diferencia precio
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {resells.map((row: any, rowIndex: number) => {
            return (
              <TableRow key={rowIndex} className={classes.tableRow}>
                <TableCell className={classes.actionsCell}>
                  <Box display="flex" justifyContent="start">
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => deleteRow(rowIndex)}
                    >
                      <Delete className={classes.deleteButton} />
                    </IconButton>
                    {rowIndex === 0 && (
                      <LightTooltip
                        placement="right"
                        title={
                          <Typography style={{ padding: 12 }}>
                            Asignar mismas condiciones a todas
                          </Typography>
                        }
                      >
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => assignConditionsForAllResells()}
                        >
                          <AssignmentTurnedIn
                            className={classes.assignButton}
                          />
                        </IconButton>
                      </LightTooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>{row.folio}</TableCell>
                <TableCell className={classes.tableCellTasa}>
                  {row.factoringRequestId
                    ? cellFormat(row.factoringRequestId, 'numeric-id')
                    : 'N/A'}
                </TableCell>
                <TableCell className={classes.tableCellNames}>
                  {row.businessName}
                </TableCell>
                <TableCell className={classes.tableCellNames}>
                  {row.stakeholderName}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box className={classes.amountCell} textAlign="right">
                    <Chip
                      label={formatMoney(row.amount)}
                      className={classes.chip}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {formatDate(row.paymentDate)}
                </TableCell>
                <TableCell className={classes.tableCellTasa}>
                  <TextField
                    value={getDisplayValue(row.rate)}
                    onChange={(e) => handleChangeRate(e, rowIndex)}
                    onFocus={(e) => {
                      if (e.target.value === '0') {
                        e.target.select();
                      }
                    }}
                    InputProps={{ classes: { input: classes.textField } }}
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <TextField
                    value={formatMoney(row.operationCostFixed)}
                    onChange={(e) =>
                      handleChangeOperationCostFixed(e, rowIndex)
                    }
                    InputProps={{ classes: { input: classes.textField } }}
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <TextField
                    value={row.cessionDate}
                    type="date"
                    onChange={(e) =>
                      handleChangeCessionDate(e.target.value, rowIndex)
                    }
                    InputProps={{ classes: { input: classes.textField } }}
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <TextField
                    value={row.startDate}
                    type="date"
                    onChange={(e) =>
                      handleChangeDate(e.target.value, 'startDate', rowIndex)
                    }
                    InputProps={{ classes: { input: classes.textField } }}
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <TextField
                    value={row.tempEndDate}
                    type="date"
                    onChange={(e) =>
                      handleChangeTempDate(e, 'tempEndDate', rowIndex)
                    }
                    onBlur={() =>
                      handleChangeDate(
                        resells[rowIndex].tempEndDate,
                        'endDate',
                        rowIndex,
                      )
                    }
                    InputProps={{ classes: { input: classes.textField } }}
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Autocomplete
                    key={row.fundId || `fund-${rowIndex}`} // Clave única para forzar re-render
                    options={funds}
                    value={row.fund}
                    getOptionLabel={(option: any) =>
                      `${option.name} | ${option.id}`
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          classes: { input: classes.textFieldAutocomplete },
                        }}
                      />
                    )}
                    onChange={(e, value) =>
                      handleChangeFund(e, value, rowIndex)
                    }
                    fullWidth
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box className={classes.daysCell}>
                    <Chip
                      label={`${row.days || 0} días`}
                      className={classes.chip}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box className={classes.rateCostCell} textAlign="right">
                    <Chip
                      label={formatMoney(row.rateCost)}
                      className={classes.chip}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResellsTable;
